import {
  BackupStatus,
  ConditionOperatorEnum,
  EonAccountType,
  type Condition,
} from '@repo/api-gw-sdk';

import { BACKUP_STATUS_PROPERTY } from '../queryBuilder/propertyNames';

export const NON_POSTURE_BACKUP_STATUSES: BackupStatus[] = [
  BackupStatus.ExcludedFromBackup,
  BackupStatus.Disconnected,
  BackupStatus.Unsupported,
  BackupStatus.LimitExceeded,
];

// A free protection account is trying Eon within its offer's limits, so the inventory shows what those limits leave
// out of scope instead of hiding it.
const FREE_PROTECTION_INVENTORY_REVEALED_BACKUP_STATUSES = [
  BackupStatus.LimitExceeded,
];
const FREE_PROTECTION_INVENTORY_HIDDEN_BACKUP_STATUSES =
  NON_POSTURE_BACKUP_STATUSES.filter(
    (status) =>
      !FREE_PROTECTION_INVENTORY_REVEALED_BACKUP_STATUSES.includes(status)
  );
const NO_REVEALED_BACKUP_STATUSES: BackupStatus[] = [];

const isFreeProtection = (accountType: EonAccountType | undefined) =>
  accountType === EonAccountType.EXTERNAL_FREE_PROTECTION;

export const inventoryHiddenBackupStatuses = (
  accountType: EonAccountType | undefined
): BackupStatus[] =>
  isFreeProtection(accountType)
    ? FREE_PROTECTION_INVENTORY_HIDDEN_BACKUP_STATUSES
    : NON_POSTURE_BACKUP_STATUSES;

// The non-posture statuses the account's inventory shows anyway, for a scope that lists what it shows.
export const inventoryRevealedBackupStatuses = (
  accountType: EonAccountType | undefined
): BackupStatus[] =>
  isFreeProtection(accountType)
    ? FREE_PROTECTION_INVENTORY_REVEALED_BACKUP_STATUSES
    : NO_REVEALED_BACKUP_STATUSES;

/**
 * The statuses the dashboard's server-side aggregates leave out. A slice link has to
 * re-apply this list, otherwise the inventory it opens is longer than the slice it
 * came from. Classification failed and access lost are here but not above: the
 * inventory shows both without a toggle, the dashboard just doesn't count them —
 * neither has a BackedBy bucket to be counted into.
 */
export const DASHBOARD_EXCLUDED_BACKUP_STATUSES: BackupStatus[] = [
  ...NON_POSTURE_BACKUP_STATUSES,
  BackupStatus.ClassificationFailed,
  BackupStatus.AccessLost,
];

/**
 * The hidden statuses the inventory's filter bar carries as an ordinary filter.
 * Unsupported is missing on purpose: the inventory settings toggle owns it, so it
 * is excluded by the view rather than offered as a status to filter by.
 */
export const FILTER_BAR_EXCLUDED_BACKUP_STATUSES: BackupStatus[] =
  NON_POSTURE_BACKUP_STATUSES.filter(
    (status) => status !== BackupStatus.Unsupported
  );

export const createBackupStatusNotInCondition = (
  values: string[]
): Condition => ({
  property: BACKUP_STATUS_PROPERTY,
  operator: ConditionOperatorEnum.NotIn,
  value: values,
});

export const createBackupStatusInCondition = (values: string[]): Condition => ({
  property: BACKUP_STATUS_PROPERTY,
  operator: ConditionOperatorEnum.In,
  value: values,
});

/**
 * The statuses that make an on-demand backup request a no-op: the resource is gone,
 * unreachable, out of scope, or cannot be assessed. The backup API rejects every one of
 * them, so the console has to agree with it in all three places a backup can be started —
 * the single-resource action bar, the bulk menu's eligibility check, and the dialog's
 * request loop. Keeping one list is what stops a new status reaching only some of them.
 */
const UNBACKABLE_BACKUP_STATUSES: BackupStatus[] = [
  BackupStatus.Terminated,
  BackupStatus.Disconnected,
  BackupStatus.AccessLost,
  BackupStatus.Unsupported,
  BackupStatus.ClassificationFailed,
  BackupStatus.LimitExceeded,
];

export const isUnbackableStatus = (status: BackupStatus | undefined): boolean =>
  status !== undefined && UNBACKABLE_BACKUP_STATUSES.includes(status);
